import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_CONFIG } from '../config';
import { tryRefreshToken, triggerAuthFailure } from './authBridge';
import type {
  Profile,
  ProfilePhoto,
  ProfilesResponse,
  ProfileQueryParams,
  GotraMasterResponse,
  NadiMasterResponse,
  LabelItem,
  StateResponse,
  DistrictResponse,
  EducationMasterResponse,
  CommunitySurnameResponse,
  ProfessionMasterResponse,
  ContactDetails,
  UserPreference,
  MembershipStatus,
  MembershipPlan,
  CreatePaymentOrderResponse,
  InterestResponse,
  ProfileViewerResponse,
  ShortlistResponse,
  ContactViewedResponse,
  ViewedProfilePageResponse,
  FeedResponse,
  FaqApiItem,
  KundaliMatchResult,
} from '../types';

const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: { 'Content-Type': 'application/json' },
});

// Attach Bearer token to every request
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await AsyncStorage.getItem('auth_token');
 
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Tracks whether a token refresh is already in flight so parallel 401s don't each trigger their own refresh
let isRefreshing = false;
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

const drainQueue = (error: unknown, token: string | null) => {
  refreshQueue.forEach(p => (error ? p.reject(error) : p.resolve(token!)));
  refreshQueue = [];
};

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

api.interceptors.response.use(
  (res: AxiosResponse) => res,
  async (err: unknown) => {
    const axiosErr = err as { response?: { status?: number; data?: { message?: string; errorCode?: string; errors?: { en?: string }[] } }; config?: RetryConfig };
    const status = axiosErr?.response?.status;
    const config = axiosErr?.config;

    // ── Token expired: try a silent refresh then replay the request ────────────
    if (status === 401 && config && !config._retry) {
      if (isRefreshing) {
        // Another refresh is in flight — queue this request and wait
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(token => {
          config.headers!.Authorization = `Bearer ${token}`;
          return api(config);
        });
      }

      config._retry = true;
      isRefreshing = true;

      try {
        const newToken = await tryRefreshToken();
        if (newToken) {
          drainQueue(null, newToken);
          config.headers!.Authorization = `Bearer ${newToken}`;
          return api(config);
        }
        // Refresh token also expired — force logout → app navigates to Login
        drainQueue(err, null);
        await triggerAuthFailure();
        return Promise.reject(err);
      } catch (refreshErr) {
        drainQueue(refreshErr, null);
        await triggerAuthFailure();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    // ── All other errors: normalise into a typed Error ─────────────────────────
    const data = axiosErr?.response?.data;
    const message = data?.message ?? data?.errors?.[0]?.en ?? 'Something went wrong';
    const error = new Error(message) as Error & { status?: number; errorCode?: string };
    error.status = status;
    error.errorCode = data?.errorCode;
    return Promise.reject(error);
  },
);



// ─── Payment & Membership APIs ────────────────────────────────────────────────

export const getMembershipStatus = (): Promise<AxiosResponse<MembershipStatus>> =>
  api.get('/payments/membership-status');

export const getPlans = (): Promise<AxiosResponse<MembershipPlan[]>> =>
  api.get('/payments/plans');

export const createPaymentOrder = (planType: string): Promise<AxiosResponse<CreatePaymentOrderResponse>> =>
  api.post('/payments/create-order', { planType });

export const verifyPayment = (data: {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
  planType: string;
}): Promise<AxiosResponse<void>> =>
  api.post('/payments/verify', data);

export const reportPaymentFailure = (data: {
  razorpayOrderId: string;
  razorpayPaymentId?: string;
  reason?: string;
}): Promise<AxiosResponse<void>> =>
  api.post('/payments/failure', data);

export const activateFreePlan = (planType: string): Promise<AxiosResponse<void>> =>
  api.post('/payments/activate-free-plan', { planType });

// ─── Interest APIs ────────────────────────────────────────────────────────────

export const sendInterest = (targetProfileId: string): Promise<AxiosResponse<void>> =>
  api.post(`/interests/${targetProfileId}`);

export const getReceivedInterests = (): Promise<AxiosResponse<InterestResponse[]>> =>
  api.get('/interests/received');

export const getSentInterests = (): Promise<AxiosResponse<InterestResponse[]>> =>
  api.get('/interests/sent');

export const getInterestStatus = (profileId: string): Promise<AxiosResponse<{ sent: boolean }>> =>
  api.get(`/interests/${profileId}/status`);

export const respondToInterest = (id: string, status: 'ACCEPTED' | 'REJECTED'): Promise<AxiosResponse<void>> =>
  api.put(`/interests/${id}/respond`, null, { params: { status } });

export const getProfileViewers = (profileId: string): Promise<AxiosResponse<ProfileViewerResponse[]>> =>
  api.get(`/profiles/${profileId}/viewers`);

// ─── Preference APIs ──────────────────────────────────────────────────────────

export const getPreferences = (): Promise<AxiosResponse<UserPreference>> =>
  api.get('/preferences');

export const savePreferences = (data: UserPreference): Promise<AxiosResponse<UserPreference>> =>
  api.post('/preferences', data);

// ─── Master data APIs ──────────────────────────────────────────────────────────

export const getStates = (): Promise<AxiosResponse<StateResponse[]>> =>
  api.get('/master/locations/states');

export const getDistricts = (stateCode: string): Promise<AxiosResponse<DistrictResponse[]>> =>
  api.get(`/master/locations/districts/${stateCode}`);

export const getEducations = (): Promise<AxiosResponse<EducationMasterResponse[]>> =>
  api.get('/master/education/active');

export const getCommunities = (): Promise<AxiosResponse<CommunitySurnameResponse[]>> =>
  api.get('/master/community');

export const getProfessions = (): Promise<AxiosResponse<ProfessionMasterResponse[]>> =>
  api.get('/master/professions');

export const getGotras = (): Promise<AxiosResponse<GotraMasterResponse[]>> => {
  
  return api.get('/master/gotra')
    .then(res => {  return res; })
    .catch(err => {  return Promise.reject(err); });
};

export const getNadis = (): Promise<AxiosResponse<NadiMasterResponse[]>> => {
  console.log('[getNadis] calling...');
  return api.get('/master/nadi')
    .then(res => {  return res })
    .catch(err => {  return Promise.reject(err) });
};

export const getLabels = (): Promise<AxiosResponse<LabelItem[]>> => {
  return api.get('/master/translations')
    .then(res => { return res; })
    .catch(err => { return Promise.reject(err); });
};

export const getFaqs = (): Promise<AxiosResponse<FaqApiItem[]>> =>
  api.get('/master/faqs');

export const getAppConfig = () =>
  api.get('/master/app-config');

// ─── Profile APIs ─────────────────────────────────────────────────────────────

export const getProfiles = (
  filters: ProfileQueryParams = {},
): Promise<AxiosResponse<ProfilesResponse>> =>
  api.get('/profiles/active', { params: filters });

export const getProfileById = (id: string): Promise<AxiosResponse<Profile>> =>
  api.get(`/profiles/${id}`);

export const getProfileByUserId = (userId: string): Promise<AxiosResponse<Profile>> =>
  api.get(`/profiles/getProfileByUserId?userId=${userId}`);

export const getProfileByEmail = (email: string): Promise<AxiosResponse<Profile>> =>
  api.get(`/profiles/email/${encodeURIComponent(email)}`);

export const viewContact = (
  profileId: string,
): Promise<AxiosResponse<ContactDetails>> =>
  api.get(`/profiles/${profileId}/viewContact`);

export const getProfileByCode = (profileCode: string): Promise<AxiosResponse<Profile>> =>
  api.get(`/profiles/code/${profileCode}`);

export const getProfilesFeed = (
  gender: string,
  page: number,
  prefTotal?: number,
  religion?: string,
): Promise<AxiosResponse<FeedResponse>> =>
  api.get('/profiles/feed', {
    params: { gender, page, ...(prefTotal !== undefined && prefTotal >= 0 ? { prefTotal } : {}), ...(religion ? { religion } : {}) },
  });
  
export const createProfile = (data: Record<string, unknown>): Promise<AxiosResponse<Profile>> =>
  api.post('/profiles', data);

export const updateProfile = (
  id: string,
  data: Record<string, unknown>,
): Promise<AxiosResponse<Profile>> => api.put(`/profiles/${id}`, data);

export const submitProfile = (id: string): Promise<AxiosResponse<Profile>> =>
  api.put(`/profiles/${id}/submit`);

export const deactivateProfile = (id: string): Promise<AxiosResponse<Profile>> =>
  api.put(`/profiles/${id}/deactivate`);

export const reactivateProfile = (id: string): Promise<AxiosResponse<Profile>> =>
  api.put(`/profiles/${id}/reactivate`);

export const requestProfileDeletion = (id: string): Promise<AxiosResponse<Profile>> =>
  api.put(`/profiles/${id}/request-deletion`);

export const cancelProfileDeletion = (id: string): Promise<AxiosResponse<Profile>> =>
  api.put(`/profiles/${id}/cancel-deletion`);

export const getPhotoConfig = (): Promise<AxiosResponse<{ maxPhotos: number }>> =>
  api.get('/profiles/photos/config');

// All photos (owner view — approved + pending)
export const getProfilePhotos = (profileId: string): Promise<AxiosResponse<ProfilePhoto[]>> => {
  //console.log('[getProfilePhotos] calling for profileId:', profileId);
  return api.get(`/profiles/photos/profile/${profileId}`)
    .then(res => {  return res; })
    .catch(err => {  return Promise.reject(err); });
};

// Approved photos only (public view — ProfileDetailScreen)
export const getApprovedProfilePhotos = (profileId: string): Promise<AxiosResponse<ProfilePhoto[]>> => {
 // console.log('[getApprovedPhotos] calling for profileId:', profileId);
  return api.get(`/profiles/photos/profile/${profileId}/approved`)
    .then(res => {  return res; })
    .catch(err => {  return Promise.reject(err); });
};

export const uploadProfilePhoto = (
  formData: FormData,
): Promise<AxiosResponse<ProfilePhoto>> =>
  api.post('/profiles/photos/add', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const updateProfilePhoto = (
  photoId: string,
  formData: FormData,
): Promise<AxiosResponse<ProfilePhoto>> =>
  api.put(`/profiles/photos/${photoId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const setMainPhoto = (photoId: string): Promise<AxiosResponse<ProfilePhoto>> =>
  api.put(`/profiles/photos/${photoId}/set-main`);

export const deleteProfilePhoto = (photoId: string): Promise<AxiosResponse<void>> =>
  api.delete(`/profiles/photos/${photoId}`);

// ─── Shortlist APIs ───────────────────────────────────────────────────────────

export const addToShortlist = (profileId: string): Promise<AxiosResponse<{ added: boolean; shortlisted: boolean }>> =>
  api.post(`/shortlist/${profileId}`);

export const removeFromShortlist = (profileId: string): Promise<AxiosResponse<void>> =>
  api.delete(`/shortlist/${profileId}`);

export const getShortlistStatus = (profileId: string): Promise<AxiosResponse<{ shortlisted: boolean }>> =>
  api.get(`/shortlist/${profileId}/status`);

export const getShortlist = (): Promise<AxiosResponse<ShortlistResponse[]>> =>
  api.get('/shortlist');

export const getContactsViewed = (): Promise<AxiosResponse<ContactViewedResponse[]>> =>
  api.get('/memberships/my/contacts-viewed');

export const getViewedProfiles = (page: number): Promise<AxiosResponse<ViewedProfilePageResponse>> =>
  api.get('/memberships/my/viewed-profiles', { params: { page } });

// ─── Block & Report APIs ──────────────────────────────────────────────────────

export const blockProfile = (profileId: string): Promise<AxiosResponse<void>> =>
  api.post(`/block/${profileId}`);

export const unblockProfile = (profileId: string): Promise<AxiosResponse<void>> =>
  api.delete(`/block/${profileId}`);

export const getBlockStatus = (profileId: string): Promise<AxiosResponse<{ blocked: boolean }>> =>
  api.get(`/block/${profileId}/status`);

export const getBlockedProfiles = (): Promise<AxiosResponse<string[]>> =>
  api.get('/block');

export const reportProfile = (
  profileId: string,
  data: { reason: string; details?: string },
): Promise<AxiosResponse<void>> =>
  api.post(`/report/${profileId}`, data);

// ─── LinkedIn OAuth APIs ──────────────────────────────────────────────────────

export const getLinkedInAuthUrl = (): Promise<AxiosResponse<{ authUrl: string }>> =>
  api.get('/linkedin/auth-url');

export const removeLinkedInVerification = (profileId: string): Promise<AxiosResponse<void>> =>
  api.delete(`/linkedin/verify/${profileId}`);

export const removeMyLinkedInVerification = (): Promise<AxiosResponse<void>> =>
  api.delete('/linkedin/my-verification');

// ─── Kundali APIs ─────────────────────────────────────────────────────────────

export const getKundaliMatch = (
  profileId: string,
  targetProfileId: string,
): Promise<AxiosResponse<KundaliMatchResult>> =>
  api.get(`/profiles/${profileId}/kundali-match/${targetProfileId}`);

export default api;
